import React, { useEffect, useState } from 'react';
import { useVideoGeneration } from '../contexts/VideoGenerationContext';
import './VideoGenerationModal.css';

const VideoGenerationModal = () => {
  const {
    isGenerating,
    isModalMinimized,
    projectTitle,
    progress,
    status,
    videoUrl,
    error,
    estimatedTimeRemaining,
    minimizeModal,
    maximizeModal,
    clearGeneration
  } = useVideoGeneration();

  const [showDownloadNotification, setShowDownloadNotification] = useState(false);

  // Show notification when video is ready
  useEffect(() => {
    if (status === 'completed' && videoUrl) {
      setShowDownloadNotification(true);
      // Auto-hide notification after 5 seconds
      const timer = setTimeout(() => setShowDownloadNotification(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [status, videoUrl]);

  // Don't render if not generating and no completed/failed state
  if (!isGenerating && status !== 'completed' && status !== 'failed') {
    return null;
  }

  // Format time remaining
  const formatTimeRemaining = (milliseconds) => {
    if (!milliseconds || milliseconds <= 0) return 'Calculating...';
    
    const seconds = Math.ceil(milliseconds / 1000);
    if (seconds < 60) return `${seconds}s remaining`;
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s remaining`;
  };

  // Get status color
  const getStatusColor = () => {
    switch (status) {
      case 'completed': return '#28a745';
      case 'failed': return '#dc3545';
      case 'processing': return '#007acc';
      default: return '#ffc107';
    }
  };

  // Get status text
  const getStatusText = () => {
    switch (status) {
      case 'pending': return 'Initializing...';
      case 'processing': return 'Generating video...';
      case 'completed': return 'Video ready!';
      case 'failed': return 'Generation failed';
      default: return 'Processing...';
    }
  };

  // Minimized view
  if (isModalMinimized) {
    return (
      <div className="video-generation-modal minimized">
        <div className="minimized-content">
          <div className="minimized-info">
            <div className="minimized-title">
              <span className="status-dot" style={{ backgroundColor: getStatusColor() }}></span>
              Generating "{projectTitle}"
            </div>
            <div className="minimized-progress">
              <div className="progress-bar-mini">
                <div 
                  className="progress-fill-mini" 
                  style={{ 
                    width: `${progress}%`,
                    backgroundColor: getStatusColor()
                  }}
                ></div>
              </div>
              <span className="progress-text-mini">{progress}%</span>
            </div>
          </div>
          <div className="minimized-controls">
            <button 
              className="expand-button" 
              onClick={maximizeModal}
              title="Expand"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15,3 21,3 21,9"></polyline>
                <polyline points="9,21 3,21 3,15"></polyline>
                <line x1="21" y1="3" x2="14" y2="10"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
              </svg>
            </button>
            {(status === 'completed' || status === 'failed') && (
              <button 
                className="close-button-mini" 
                onClick={clearGeneration}
                title="Close"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Full modal view
  return (
    <>
      <div className="video-generation-modal-overlay" onClick={minimizeModal}></div>
      <div className="video-generation-modal expanded">
        <div className="modal-header">
          <h3>Video Generation</h3>
          <div className="modal-header-controls">
            <button 
              className="minimize-button" 
              onClick={minimizeModal}
              title="Minimize"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="4,14 10,14 10,20"></polyline>
                <polyline points="20,10 14,10 14,4"></polyline>
                <line x1="14" y1="10" x2="21" y2="3"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
              </svg>
            </button>
            {(status === 'completed' || status === 'failed') && (
              <button 
                className="close-button" 
                onClick={clearGeneration}
                title="Close"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="modal-content">
          <div className="project-info">
            <h4>"{projectTitle}"</h4>
            <div className="status-info">
              <span className="status-indicator" style={{ backgroundColor: getStatusColor() }}>
                {getStatusText()}
              </span>
              {isGenerating && estimatedTimeRemaining && (
                <span className="time-remaining">
                  {formatTimeRemaining(estimatedTimeRemaining)}
                </span>
              )}
            </div>
          </div>

          <div className="progress-section">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${progress}%`,
                  backgroundColor: getStatusColor()
                }}
              ></div>
            </div>
            <div className="progress-details">
              <span className="progress-percentage">{progress}%</span>
              {status === 'processing' && (
                <span className="progress-status">Processing timeline items...</span>
              )}
            </div>
          </div>

          {/* Success state */}
          {status === 'completed' && videoUrl && (
            <div className="success-section">
              <div className="success-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20,6 9,17 4,12"></polyline>
                </svg>
              </div>
              <p>Your video has been generated successfully!</p>
              <div className="action-buttons">
                <a 
                  href={videoUrl} 
                  download 
                  className="download-button"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7,10 12,15 17,10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download Video
                </a>
                <button 
                  className="view-button"
                  onClick={() => window.open(videoUrl, '_blank')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="5,3 19,12 5,21"></polygon>
                  </svg>
                  View Video
                </button>
              </div>
            </div>
          )}

          {/* Error state */}
          {status === 'failed' && (
            <div className="error-section">
              <div className="error-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </div>
              <p>Video generation failed</p>
              {error && <p className="error-message">{error}</p>}
              <button 
                className="retry-button"
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
            </div>
          )}

          {/* Processing animation */}
          {isGenerating && (
            <div className="processing-animation">
              <div className="spinner"></div>
              <p>Please keep this tab open while your video is being generated...</p>
              <div className="generation-notice">
                <p><strong>Note:</strong> Other app features may be temporarily unavailable during video generation.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Download notification */}
      {showDownloadNotification && (
        <div className="download-notification">
          <div className="notification-content">
            <div className="notification-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20,6 9,17 4,12"></polyline>
              </svg>
            </div>
            <div className="notification-text">
              <strong>Video Ready!</strong>
              <p>"{projectTitle}" has been generated successfully.</p>
            </div>
            <button 
              className="notification-close"
              onClick={() => setShowDownloadNotification(false)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoGenerationModal;